import type * as React from 'react'

import clsx from 'clsx'

import {
  ExpandIcon,
  ExternalLinkIcon,
  Lightbox,
  Link,
  Text,
  Tooltip,
  UnstyledButton,
  VisuallyHidden,
} from '@buffer-mono/popcorn'
import type { LightboxProps } from '@buffer-mono/popcorn'

import { MediaCombinedPreview } from '~publish/components/MediaCombinedPreview'
import { readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { normalizeNetworkName } from '~publish/helpers/channels'
import { isNotNull, isOfType } from '~publish/helpers/typeGuards'
import { useIsVideoUrl } from '~publish/hooks/useIsVideoUrl'

import { usePostData } from '../PostCardContext'
import { getAltText } from './helpers'
import { PostMediaAsset, PostMediaAsset_Asset } from './PostMediaAsset'
import { PostMediaLightbox } from './PostMediaLightbox'

import styles from './PostMedia.module.css'

import type { JSX } from 'react'

const mapAssetsToLightboxSlides = (
  assets: FragmentOf<typeof PostMediaAsset_Asset>[],
): LightboxProps['slides'] => {
  return readFragment(PostMediaAsset_Asset, assets)
    .map((asset) => {
      if (isOfType(asset, 'VideoAsset')) {
        // Remap video/quicktime to video/mp4: .mov files from iPhones
        // typically contain H.264/AAC (same codecs as MP4), but browsers
        // reject <source type="video/quicktime"> without attempting decode.
        const mimeType =
          asset.mimeType === 'video/quicktime' ? 'video/mp4' : asset.mimeType
        return {
          type: 'video' as const,
          poster: asset.thumbnail,
          width: asset.video.width,
          height: asset.video.height,
          sources: [{ src: asset.source, type: mimeType }],
        }
      }

      if (isOfType(asset, 'ImageAsset')) {
        return {
          type: 'image' as const,
          src: asset.source ?? asset.thumbnail,
          alt: getAltText(asset),
          width: asset.image.width,
          height: asset.image.height,
        }
      }
      if (isOfType(asset, 'DocumentAsset')) {
        return {
          type: 'pdf' as const,
          src: asset.source ?? asset.thumbnail,
          alt: getAltText(asset),
        }
      }
      return null
    })
    .filter(isNotNull)
}

const ClickableAsset = ({
  children,
  index,
}: {
  children: React.ReactNode
  index: number
}): JSX.Element => (
  <div className={styles.mediaWrapper}>
    {children}
    <Lightbox.Trigger index={index} data-no-dnd asChild>
      <UnstyledButton className={styles.viewMedia}>
        <ExpandIcon />
        <VisuallyHidden>View</VisuallyHidden>
      </UnstyledButton>
    </Lightbox.Trigger>
  </div>
)

const VideoClickableAsset = ({
  children,
  index,
  videoSource,
  fallbackUrl,
  channelService,
}: {
  children: React.ReactNode
  index: number
  videoSource: string | null | undefined
  fallbackUrl: string | null | undefined
  channelService: string
}): JSX.Element => {
  const { isVideo, isLoading } = useIsVideoUrl({ url: videoSource })

  if (isLoading) {
    return <div className={styles.mediaWrapper}>{children}</div>
  }

  if (isVideo) {
    return <ClickableAsset index={index}>{children}</ClickableAsset>
  }

  if (!fallbackUrl) {
    return <div className={styles.mediaWrapper}>{children}</div>
  }

  const networkName = normalizeNetworkName(channelService)

  return (
    <Tooltip
      content={
        <Text as="span" size="sm">
          This video was uploaded natively on {networkName}. Watch it there.
        </Text>
      }
      delay={1000}
    >
      <Link
        href={fallbackUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.mediaWrapperLink}
        data-no-dnd
        aria-label={`Watch on ${networkName}`}
      >
        {children}
        <span className={styles.viewMedia}>
          <ExternalLinkIcon />
        </span>
      </Link>
    </Tooltip>
  )
}

type PostMediaPreviewProps = {
  className?: string
  assets: FragmentOf<typeof PostMediaAsset_Asset>[]
}

const PostMediaBentoBoxPreview = ({
  assets,
  className,
}: PostMediaPreviewProps): JSX.Element | null => {
  const { externalLink, channel } = usePostData()

  if (!assets.length) {
    return null
  }

  const slides = mapAssetsToLightboxSlides(assets)

  return (
    <PostMediaLightbox slides={slides}>
      <MediaCombinedPreview className={clsx(styles.bento, className)}>
        {assets.map((asset, index) => {
          const {
            thumbnail,
            source,
            __typename: assetType,
          } = readFragment(PostMediaAsset_Asset, asset)

          if (assetType === 'VideoAsset') {
            return (
              <VideoClickableAsset
                key={thumbnail}
                index={index}
                videoSource={source}
                fallbackUrl={externalLink}
                channelService={channel.service}
              >
                <PostMediaAsset asset={asset} />
              </VideoClickableAsset>
            )
          }

          return (
            <ClickableAsset key={thumbnail} index={index}>
              <PostMediaAsset asset={asset} />
            </ClickableAsset>
          )
        })}
      </MediaCombinedPreview>
    </PostMediaLightbox>
  )
}

PostMediaBentoBoxPreview.displayName = 'PostMediaBentoBoxPreview'

const PostMediaRowPreview = ({
  assets,
  className,
}: PostMediaPreviewProps): JSX.Element | null => {
  const { externalLink, channel } = usePostData()

  if (!assets.length) {
    return null
  }

  const slides = mapAssetsToLightboxSlides(assets)

  return (
    <PostMediaLightbox slides={slides}>
      <div className={clsx(styles.row, className)}>
        {assets.map((asset, index) => {
          const {
            id,
            source,
            __typename: assetType,
          } = readFragment(PostMediaAsset_Asset, asset)

          if (assetType === 'VideoAsset') {
            return (
              <VideoClickableAsset
                key={id}
                index={index}
                videoSource={source}
                fallbackUrl={externalLink}
                channelService={channel.service}
              >
                <PostMediaAsset asset={asset} />
              </VideoClickableAsset>
            )
          }

          return (
            <ClickableAsset key={id} index={index}>
              <PostMediaAsset asset={asset} />
            </ClickableAsset>
          )
        })}
      </div>
    </PostMediaLightbox>
  )
}

PostMediaRowPreview.displayName = 'PostMediaRowPreview'

export { PostMediaBentoBoxPreview, PostMediaRowPreview }
